import axios from 'axios';
import config from '../config/api.config';

// ============================================
// API Configuration
// ============================================

const api = axios.create({
  baseURL: config.API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: config.TIMEOUT,
  withCredentials: config.WITH_CREDENTIALS,
});

// ============================================
// Request Interceptor
// ============================================
api.interceptors.request.use(
  (requestConfig) => {
    const token = localStorage.getItem('token');
    if (token && token !== 'undefined' && token !== 'null' && token.trim() !== '') {
      requestConfig.headers.Authorization = `Bearer ${token}`;
      requestConfig.headers['X-Auth-Token'] = token;
      // Also attach token in params for Apache environments that strip auth headers
      requestConfig.params = { ...(requestConfig.params || {}), token };
    }

    // Ensure relative URL resolves properly against baseURL (prevent stripping backend_api.php)
    if (requestConfig.url && requestConfig.url.startsWith('/') && !requestConfig.url.startsWith('//')) {
      requestConfig.url = requestConfig.url.substring(1);
    }
    
    // Log in development
    if (config.ENVIRONMENT === 'development') {
      console.log(`[API] ${requestConfig.method?.toUpperCase()} ${requestConfig.url}`);
    }
    
    return requestConfig;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// ============================================
// Response Interceptor
// ============================================
api.interceptors.response.use(
  (response) => {
    // Log in development
    if (config.ENVIRONMENT === 'development') {
      console.log(`[API Response] ${response.config.url}`, response.status);
    }
    return response;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          console.warn('Authentication required or session expired');
          // Only redirect if explicitly on a protected path and login wasn't just attempted
          if (!window.location.pathname.includes('/admin/login') && !window.location.pathname.includes('/login') && !localStorage.getItem('token')) {
            window.location.href = '/admin/login';
          }
          break;
        case 403:
          console.error('Forbidden: Insufficient permissions');
          break;
        case 404:
          console.error('Resource not found:', error.config?.url);
          break;
        case 422:
          console.error('Validation error:', data);
          break;
        case 500:
          console.error('Server error:', data);
          break;
        default:
          console.error(`API Error ${status}:`, data);
      }
      
      // Return user-friendly error message
      const errorMessage = data?.error || data?.message || 'An error occurred';
      error.message = errorMessage;
      
    } else if (error.request) {
      // Network error
      console.error('Network Error: Server not responding');
      error.message = 'Cannot connect to server. Please check your connection.';
    }
    
    return Promise.reject(error);
  }
);

// ============================================
// Auth Helper Functions
// ============================================

export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  return !!token;
};

export const getCurrentUser = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    return payload;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = '/admin/login';
};

// ============================================
// API Endpoint Groups
// ============================================

// Health check
export const checkApiHealth = async () => {
  try {
    const response = await api.get('/health');
    return response.data?.status === 'healthy';
  } catch (error) {
    console.error('API health check failed:', error);
    return false;
  }
};

// ============================================
// Auth endpoints
// ============================================
export const authAPI = {
  login: (username, password) => {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    
    return api.post('/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  },
  verify: () => api.post('/auth/verify'),
  createAdmin: (data) => api.post('/admin/create-admin', data),
};

// ============================================
// Secondary School Applications endpoints
// ============================================
export const applicationsAPI = {
  create: (data) => api.post('/applications', data),
  track: (regNumber) => api.get(`/applications/track/${encodeURIComponent(regNumber)}`),
  getAll: () => api.get('/admin/applications'),
  getStats: () => api.get('/admin/applications/stats'),
  getDetails: (id) => api.get(`/admin/applications/${id}`),
  updateStatus: (id, status) => api.put(`/admin/applications/${id}/status`, { status }),
  bulkAdmit: (applicationIds = []) => api.post('/admin/applications/bulk-admit', { application_ids: applicationIds }),
  delete: (id) => api.delete(`/admin/applications/${id}`),
};

// ============================================
// Students endpoints (List, Edit, Delete, Promote)
// ============================================
export const studentsAPI = {
  getAll: (params = {}) => api.get('/admin/students', { params }),
  search: (registration) => api.get(`/students/search?registration=${encodeURIComponent(registration)}`),
  getById: (id) => api.get(`/admin/students/${id}`),
  create: (data) => api.post('/admin/students', data),
  update: (id, data) => api.put(`/admin/students/${id}`, data),
  delete: (id) => api.delete(`/admin/students/${id}`),
  promote: (fromClass, toClass) => api.post('/admin/students/promote', { from_class: fromClass, to_class: toClass }),
  getSubjects: (studentId) => api.get(`/students/${studentId}/subjects`),
  getMarks: (studentId) => api.get(`/students/${studentId}/marks`),
  export: () => api.get('/admin/students/export', { responseType: 'blob' }),
};

// ============================================
// Teachers Management endpoints
// ============================================
export const teachersAPI = {
  getAll: () => api.get('/admin/teachers'),
  create: (data) => api.post('/admin/teachers', data),
  delete: (id) => api.delete(`/admin/teachers/${id}`),
};

// ============================================
// Admission Window Settings
// ============================================
export const admissionSettingsAPI = {
  get: () => api.get('/admission-settings'),
  update: (data) => api.post('/admin/admission-settings', data),
};

// ============================================
// Secondary School Marks endpoints
// ============================================
export const marksAPI = {
  create: (data) => api.post('/marks', data),
  bulk: (data) => api.post('/marks/bulk', data),
  update: (id, data) => api.put(`/marks/${id}`, data),
  getForStudent: (studentId) => api.get(`/students/${studentId}/marks`),
  getByExamType: (studentId, examTypeId) => api.get(`/marks?student_id=${studentId}&exam_type_id=${examTypeId}`),
};

// ============================================
// Primary/English Medium School Endpoints
// ============================================
export const primaryAPI = {
  // Admission
  submitApplication: (data) => api.post('/primary/admissions', data),
  
  // Student management
  getStudent: (registration) => api.get(`/primary/student`, { 
    params: { registration: registration } 
  }),
  getStudentsByClass: (className) => api.get(`/primary/students`, {
    params: { class: className }
  }),
  getAllStudents: () => api.get('/primary/students/all'),
  
  // Results
  getResults: (registration, term = null, year = null) => api.get(`/primary/results`, {
    params: { 
      registration: registration,
      term: term,
      year: year 
    }
  }),
  
  // Marks entry
  enterMarks: (data) => api.post('/primary/marks', data),
  getMarks: (studentId, term = null, year = null) => api.get(`/primary/marks`, {
    params: {
      student_id: studentId,
      term: term,
      year: year
    }
  }),
  
  // Subjects (flexible - teachers can add as needed)
  getSubjects: () => api.get('/primary/subjects'),
  addSubject: (subjectName) => api.post('/primary/subjects', { name: subjectName }),
  
  // Class management
  getClasses: () => api.get('/primary/classes'),
  getStudentsByClassStream: (className, stream = null) => api.get(`/primary/students/${className}`, {
    params: { stream: stream }
  }),
  
  // Reports
  generateClassReport: (className, term, year) => api.get(`/primary/reports/class/${className}`, {
    params: { term: term, year: year }
  }),
  generateStudentReportCard: (studentId, term, year) => api.get(`/primary/reports/student/${studentId}`, {
    params: { term: term, year: year },
    responseType: 'blob'
  }),
  
  // Statistics
  getStats: () => api.get('/primary/stats'),
  getClassStats: (className) => api.get(`/primary/stats/class/${className}`),
  
  // Fee management
  getFeeStructure: (classLevel = null) => api.get('/primary/fees', {
    params: { class_level: classLevel }
  }),
  getRequirements: (studentType = null) => api.get('/primary/requirements', {
    params: { student_type: studentType }
  }),
};

// ============================================
// Results endpoints (public)
// ============================================
export const resultsAPI = {
  // Secondary school results
  getPublicResults: (registration) => 
    api.get(`/public/student-results?registration=${encodeURIComponent(registration)}`),
  
  // Primary school results
  getPrimaryResults: (registration, term = null, year = null) => 
    api.get(`/primary/results`, {
      params: { 
        registration: registration,
        term: term,
        year: year 
      }
    }),
  
  // Unified results (works for both)
  getUnifiedResults: (registration, schoolType = 'secondary') => 
    api.get(`/public/unified-results`, {
      params: { 
        registration: registration,
        school_type: schoolType 
      }
    }),
};

// ============================================
// Subjects endpoints (Secondary)
// ============================================
export const subjectsAPI = {
  getAll: (formLevel = null) => {
    const url = formLevel ? `/subjects?form_level=${formLevel}` : '/subjects';
    return api.get(url);
  },
  getByForm: (formLevel) => api.get(`/subjects?form_level=${formLevel}`),
  getCombinations: () => api.get('/subjects/combinations'),
  create: (data) => api.post('/subjects', data),
  delete: (id) => api.delete(`/subjects/${id}`),
};

// ============================================
// Terms endpoints
// ============================================
export const termsAPI = {
  getAll: () => api.get('/terms'),
  getCurrent: () => api.get('/terms/current'),
  getByAcademicYear: (yearId) => api.get(`/terms?academic_year_id=${yearId}`),
};

// ============================================
// Academic Years endpoints
// ============================================
export const academicYearsAPI = {
  getAll: () => api.get('/academic-years'),
  getCurrent: () => api.get('/academic-years/current'),
  setCurrent: (yearId) => api.put(`/academic-years/${yearId}/set-current`),
};

// ============================================
// Exam types endpoints
// ============================================
export const examTypesAPI = {
  getAll: () => api.get('/exam-types'),
  getByWeight: (minWeight, maxWeight) => api.get(`/exam-types?min_weight=${minWeight}&max_weight=${maxWeight}`),
};

// ============================================
// Dashboard endpoints
// ============================================
export const dashboardAPI = {
  getStats: () => api.get('/admin/applications/stats'),
  getPrimaryStats: () => api.get('/primary/stats'),
  getCombinedStats: () => api.get('/admin/combined-stats'),
  getRecentActivities: () => api.get('/admin/recent-activities'),
};

// ============================================
// Admin endpoints
// ============================================
export const adminAPI = {
  createAdmin: (data) => api.post('/admin/create-admin', data),
  getAllAdmins: () => api.get('/admin/users'),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  getSystemLogs: () => api.get('/admin/logs'),
  getBackup: () => api.get('/admin/backup', { responseType: 'blob' }),
};

// ============================================
// Settings endpoints
// ============================================
export const settingsAPI = {
  getSchoolInfo: () => api.get('/settings/school-info'),
  updateSchoolInfo: (data) => api.put('/settings/school-info', data),
  getAcademicSettings: () => api.get('/settings/academic'),
  updateAcademicSettings: (data) => api.put('/settings/academic', data),
  getNotificationSettings: () => api.get('/settings/notifications'),
  updateNotificationSettings: (data) => api.put('/settings/notifications', data),
};

// ============================================
// Reports endpoints
// ============================================
export const reportsAPI = {
  // Secondary school reports
  getApplicationsReport: (startDate, endDate, status = null) => 
    api.get('/admin/reports/applications', {
      params: { start_date: startDate, end_date: endDate, status: status }
    }),
  
  getMarksReport: (formLevel, termId, examTypeId = null) => 
    api.get('/admin/reports/marks', {
      params: { form_level: formLevel, term_id: termId, exam_type_id: examTypeId }
    }),
  
  // Primary school reports
  getPrimaryClassReport: (className, term, year) => 
    api.get('/primary/reports/class', {
      params: { class: className, term: term, year: year }
    }),
  
  getPrimaryProgressReport: (studentId, startTerm, endTerm, year) => 
    api.get('/primary/reports/progress', {
      params: { student_id: studentId, start_term: startTerm, end_term: endTerm, year: year }
    }),
  
  // Export functions
  exportToExcel: (reportType, params) => 
    api.get(`/admin/reports/export/${reportType}`, {
      params: params,
      responseType: 'blob'
    }),
  
  exportToPDF: (reportType, params) => 
    api.get(`/admin/reports/pdf/${reportType}`, {
      params: params,
      responseType: 'blob'
    }),
};

// ============================================
// Public/General endpoints
// ============================================
export const publicAPI = {
  getFeeStructure: (level = null) => api.get('/public/fee-structure', { params: { level } }),
  getSchoolCalendar: (year = null) => api.get('/public/calendar', { params: { year } }),
  getAnnouncements: () => api.get('/public/announcements'),
  getContactInfo: () => api.get('/public/contact'),
  submitInquiry: (data) => api.post('/public/inquiry', data),
};

// ============================================
// Utility function to check API connection
// ============================================
export const testApiConnection = async () => {
  try {
    await api.get('/health');
    return true;
  } catch (error) {
    console.error('API connection failed:', error);
    return false;
  }
};

// ============================================
// Export all APIs as a single object
// ============================================
const API = {
  // Base
  api,
  checkApiHealth,
  testApiConnection,
  isAuthenticated,
  getCurrentUser,
  logout,
  
  // Modules
  auth: authAPI,
  applications: applicationsAPI,
  students: studentsAPI,
  marks: marksAPI,
  primary: primaryAPI,
  results: resultsAPI,
  subjects: subjectsAPI,
  terms: termsAPI,
  academicYears: academicYearsAPI,
  examTypes: examTypesAPI,
  dashboard: dashboardAPI,
  admin: adminAPI,
  settings: settingsAPI,
  reports: reportsAPI,
  public: publicAPI,
};

export default api;
export { API };